import { toast } from 'react-toastify';
import { IErrorModel, ISuccessResponse } from '@core/models/generic.model';
import {
    IUpdateUserRequest,
    IUser,
    IUserListingResponse,
} from '@core/models/user-listing.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import userListingService from 'services/api/user-listing.service';

// -------------------------------------------------------------------------

interface IUserListingState {
    loading: boolean;
    error: IErrorModel | undefined;
    users: IUser[];
    user: IUser | null;
}

const initialState: IUserListingState = {
    loading: false,
    error: {} as IErrorModel,
    users: [],
    user: null,
};
// -------------------------------------------------------------------------

export const getAllUsersThunk = createAsyncThunk<
    IUserListingResponse,
    { pageNumber: string; pageSize: string; userId?: string; tier?: string; accountType?: string; isAccountStatus?: boolean; email?: string },
    { rejectValue: IErrorModel }
>(`/userListing/getAllUsers`, async ({ pageNumber, pageSize, userId, tier, accountType, isAccountStatus, email }, { rejectWithValue }: any) => {
    try {
        const response: any = await userListingService.getAllUsers(pageNumber, pageSize, userId, tier, accountType, isAccountStatus, email);
        return response.data
    } catch (err: any) {
        const error = err;
        return rejectWithValue(error);
    }
});
// -------------------------------------------------------------------------

export const deActivateUserThunk = createAsyncThunk<
    any,
    { userId: string; activate: boolean },
    { rejectValue: IErrorModel }
>(`/userListing/deActivateUser`, async ({ userId, activate }, { rejectWithValue }: any) => {
    try {
        const response: any = await userListingService.deActivateUser(userId, activate);
        return response.data;
    } catch (err: any) {
        const error = err;
        return rejectWithValue(error);
    }
});
// -------------------------------------------------------------------------

export const deleteUserThunk = createAsyncThunk<
    any,
    { userId: string },
    { rejectValue: IErrorModel }
>(`/userListing/deleteUser`, async ({ userId }, { rejectWithValue }: any) => {
    try {
        const response: any = await userListingService.deleteUser(userId);
        return response.data;
    } catch (err: any) {
        const error = err;
        return rejectWithValue(error);
    }
});
// -------------------------------------------------------------------------
export const addWalletToUserThunk = createAsyncThunk<
    any,
    { accountId: string; balance: number },
    { rejectValue: IErrorModel }
>(`/userListing/addWalletToUser`, async ({ accountId, balance }, { rejectWithValue }: any) => {
    try {
        const response: any = await userListingService.addWalletToUser(accountId, balance);
        toast.success('Wallet added successfully', { autoClose: 2000 });
        return response.data;
    } catch (err: any) {
        console.log(err)
        toast.error(err?.errorMessage, { autoClose: 2000 });
        return rejectWithValue(err);
    }
});

export const updateWalletBalanceThunk = createAsyncThunk<
    any,
    { accountId: string; balance: number },
    { rejectValue: IErrorModel }
>(`/userListing/updateWalletBalance`, async ({ accountId, balance }, { rejectWithValue }: any) => {
    try {
        const response: any = await userListingService.updateWalletBalance(accountId, balance);
        toast.success('Wallet balance updated successfully', { autoClose: 2000 });
        return response.data;
    } catch (err: any) {
        toast.error(err?.errorMessage || 'Failed to update wallet balance', { autoClose: 2000 });
        return rejectWithValue(err);
    }
});
// -------------------------------------------------------------------------

export const getUserByIdThunk = createAsyncThunk<
    any,
    { userId: string },
    { rejectValue: IErrorModel }
>(`/userListing/getUserById`, async ({ userId }, { rejectWithValue }: any) => {
    try {
        const response: any = await userListingService.getUserById(userId);
        return response.data.content[0]
    } catch (err: any) {
        const error = err;
        return rejectWithValue(error);
    }
});
// -------------------------------------------------------------------------

export const updateUserThunk = createAsyncThunk<
    any,
    { data: IUpdateUserRequest },
    { rejectValue: IErrorModel }
>(`/userListing/updateUser`, async ({ data }, { rejectWithValue }: any) => {
    try {
        const response: any = await userListingService.updateUser(data);
        return response.data;
    } catch (err: any) {
        const error = err;
        return rejectWithValue(error);
    }
});
// -------------------------------------------------------------------------

export const exportUsersThunk = createAsyncThunk<
    any,
    { accountType: string, isActive: boolean },
    { rejectValue: IErrorModel }
>(`/userListing/exportUsers`, async ({ accountType, isActive }, { rejectWithValue }: any) => {
    try {
        const response: any = await userListingService.exportUsers(accountType, isActive);
        return response.data
    } catch (err: any) {
        const error = err;
        return rejectWithValue(error);
    }
});
// -------------------------------------------------------------------------

const userListingSlice = createSlice({
    name: `userListing`,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // get all users thunk
        builder.addCase(getAllUsersThunk.fulfilled, (state: any, action: any) => {
            state.users = action.payload;
            state.loading = false;
        });
        builder.addCase(getAllUsersThunk.pending, (state: any, action: any) => {
            state.loading = true;
        });
        builder.addCase(getAllUsersThunk.rejected, (state: any, action: any) => {
            state.error = action.payload;
            state.users = [];
            state.loading = false;
        });

        // deActivate user thunk
        builder.addCase(deActivateUserThunk.fulfilled, (state: any, action: any) => {
            state.loading = false;
        });
        builder.addCase(deActivateUserThunk.pending, (state: any, action: any) => {
            state.loading = false; // Don't show global loading for toggle action
        });
        builder.addCase(deActivateUserThunk.rejected, (state: any, action: any) => {
            state.error = action.payload;
            state.loading = false;
        });

        // delete user thunk
        builder.addCase(deleteUserThunk.fulfilled, (state: any, action: any) => {
            state.loading = false;
        });
        builder.addCase(deleteUserThunk.pending, (state: any, action: any) => {
            state.loading = false; // Don't show global loading for delete action
        });
        builder.addCase(deleteUserThunk.rejected, (state: any, action: any) => {
            state.error = action.payload;
            state.loading = false;
        });

        // add wallet to user thunk
        builder.addCase(addWalletToUserThunk.fulfilled, (state: any, action: any) => {
            state.loading = false;
        });
        builder.addCase(addWalletToUserThunk.pending, (state: any, action: any) => {
            state.loading = false; // Don't show global loading for wallet action
        });
        builder.addCase(addWalletToUserThunk.rejected, (state: any, action: any) => {
            state.error = action.payload;
            state.loading = false;
        });

        // get user by id thunk
        builder.addCase(getUserByIdThunk.pending, (state: any) => {
            state.loading = true;
        });
        builder.addCase(getUserByIdThunk.fulfilled, (state: any, action: any) => {
            state.loading = false;
            state.user = action.payload;
        });
        builder.addCase(getUserByIdThunk.rejected, (state: any, action: any) => {
            state.loading = false;
            state.error = action.payload;
        });

        // update user thunk
        builder.addCase(updateUserThunk.fulfilled, (state: any, action: any) => {
            state.loading = false;
        });
        builder.addCase(updateUserThunk.pending, (state: any, action: any) => {
            state.loading = false; // Don't show global loading for update action
        });
        builder.addCase(updateUserThunk.rejected, (state: any, action: any) => {
            state.error = action.payload;
            state.loading = false;
        });
    },
});
// -------------------------------------------------------------------------

export default userListingSlice;
export const userListingReducer = userListingSlice.reducer;
// -------------------------------------------------------------------------
